import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { DeleteIcon as Trash2, PlusIcon as Plus, CalendarIcon as Calendar, LocationIcon as MapPin, TimeIcon as Clock, FileEditIcon as FileEdit, EditIcon as Edit, GroupIcon as Users, CloseIcon as X, CheckCircleIcon as CheckCircle, ImageIcon, RefreshIcon as RefreshCw } from './Icons';
import EventPageBuilder from './EventPageBuilder';
import EventEditor from './EventEditor';

const EventManagement = () => {
    const { user, API_URL } = useContext(AuthContext);
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingEventPage, setEditingEventPage] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    const [viewingRegistrations, setViewingRegistrations] = useState(null); // Event ID or object
    const [registrations, setRegistrations] = useState([]);
    const [loadingRegistrations, setLoadingRegistrations] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [activeTab, setActiveTab] = useState('future'); // 'future' or 'past'
    const [selectedEventId, setSelectedEventId] = useState(null);

    // Helper: Slugify title
    const slugify = (text) => {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss')
            .replace(/[^a-z0-9 -]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        date: '',
        startTime: '',
        endTime: '',
        location: '',
        description: '',
        category: 'Sonstiges',
        link: '',
        imageUrl: '',
        editableBy: [],
        requiresConsent: false,
        isRegistrationEnabled: false,
        askHealthQuestions: false,
        requiresApproval: false, // New field
        consentFormUrl: '',
        status: 'draft', // Default to draft
        slug: '',
        registrationEmailTemplate: {
            includeConsentSection: false,
            customFields: []
        }
    });

    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        fetchEvents();
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.get(`${API_URL}/users`, config);
            setAllUsers(response.data);
        } catch (err) {
            console.error("Error fetching users:", err);
        }
    };

    const fetchEvents = async () => {
        try {
            const config = {};
            if (user && user.token) {
                config.headers = {
                    Authorization: `Bearer ${user.token}`,
                };
            }
            const response = await axios.get(`${API_URL}/events`, config);
            setEvents(response.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching events:", err);
            setError("Events konnten nicht geladen werden.");
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            
            // Auto-slugify if title changes and slug hasn't been manually edited
            if (name === 'title' && !slugManuallyEdited) {
                newData.slug = slugify(value);
            }
            
            // Mark slug as manually edited if user changes it
            if (name === 'slug') {
                setSlugManuallyEdited(true);
            }
            
            return newData;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };

            if (editingEvent) {
                // Update existing event
                await axios.put(`${API_URL}/events/${editingEvent._id}`, formData, config);
            } else {
                // Create new event
                const res = await axios.post(`${API_URL}/events`, formData, config);
                // Redirect to new event detail/edit page if created
                if (res.data && res.data._id) {
                    navigate(`/events/edit/${res.data._id}`);
                }
            }

            // Reset form
            setFormData({
                title: '',
                date: '',
                startTime: '',
                endTime: '',
                location: '',
                description: '',
                category: 'Sonstiges',
                link: '',
                imageUrl: '',
                editableBy: [],
                requiresConsent: false,
                isRegistrationEnabled: false,
                askHealthQuestions: false,
                requiresApproval: false,
                consentFormUrl: '',
                slug: '',
                registrationEmailTemplate: {
                    includeConsentSection: false,
                    customFields: []
                }
            });
            setSlugManuallyEdited(false);
            setShowCreateForm(false);
            setEditingEvent(null);
            fetchEvents();
        } catch (err) {
            console.error("Error saving event:", err);
            alert(err.response?.data?.message || "Fehler beim Speichern des Events.");
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            title: event.title,
            date: event.date.split('T')[0],
            startTime: event.startTime || '',
            endTime: event.endTime || '',
            location: event.location || '',
            description: event.description || '',
            category: event.category,
            link: event.link || '',
            imageUrl: event.imageUrl || '',
            editableBy: event.editableBy?.map(u => u._id || u) || [],
            requiresConsent: event.requiresConsent || false,
            isRegistrationEnabled: event.isRegistrationEnabled || false,
            askHealthQuestions: event.askHealthQuestions || false,
            requiresApproval: event.requiresApproval || false,
            consentFormUrl: event.consentFormUrl || '',
            status: event.status || 'published', // Default legacy to published
            slug: event.slug || '',
            registrationEmailTemplate: event.registrationEmailTemplate || { includeConsentSection: false, customFields: [] }
        });
        if (event.slug) setSlugManuallyEdited(true);
        setShowCreateForm(true);
    };

    const handleCancelEdit = () => {
        setEditingEvent(null);
        setFormData({
            title: '',
            date: '',
            startTime: '',
            endTime: '',
            location: '',
            description: '',
            category: 'Sonstiges',
            link: '',
            imageUrl: '',
            editableBy: [],
            requiresConsent: false,
            isRegistrationEnabled: false,
            askHealthQuestions: false,
            requiresApproval: false,
            consentFormUrl: '',
            status: 'draft',
            registrationEmailTemplate: { includeConsentSection: false, customFields: [] }
        });
        setShowCreateForm(false);
    };

    const handlePermissionChange = (e) => {
        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
        setFormData(prev => ({
            ...prev,
            editableBy: selectedOptions
        }));
    };

    const handlePublish = async (id) => {
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            await axios.put(`${API_URL}/events/${id}`, { status: 'published' }, config);
            fetchEvents();
        } catch (err) {
            console.error("Error publishing event:", err);
            alert("Fehler beim Veröffentlichen des Events.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Möchtest du dieses Event wirklich löschen?')) {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                };
                await axios.delete(`${API_URL}/events/${id}`, config);
                fetchEvents();
            } catch (err) {
                console.error("Error deleting event:", err);
                alert("Fehler beim Löschen des Events.");
            }
        }
    };

    const handleViewRegistrations = async (event) => {
        setViewingRegistrations(event);
        setLoadingRegistrations(true);
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.get(`${API_URL}/events/${event._id}/registrations`, config);
            setRegistrations(response.data);
        } catch (err) {
            console.error("Error fetching registrations:", err);
            alert("Fehler beim Laden der Anmeldungen.");
        } finally {
            setLoadingRegistrations(false);
        }
    };

    const handleApproveRegistration = async (registrationId) => {
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            await axios.put(`${API_URL}/events/registrations/${registrationId}/approve`, {}, config);

            // Refresh registrations list
            const updatedRegistrations = registrations.map(reg =>
                reg._id === registrationId ? { ...reg, status: 'confirmed' } : reg
            );
            setRegistrations(updatedRegistrations);
            alert("Anmeldung wurde bestätigt und E-Mail versendet.");
        } catch (err) {
            console.error("Error approving registration:", err);
            alert("Fehler beim Bestätigen der Anmeldung.");
        }
    };

    const handlePdfUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            alert('Bitte nur PDF-Dateien hochladen.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Datei ist zu groß (Max 5MB).');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setUploadingPdf(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.post(`${API_URL}/files/upload`, formData, config);
            setFormData(prev => ({ ...prev, consentFormUrl: response.data.url }));
            alert('PDF erfolgreich hochgeladen!');
        } catch (err) {
            console.error("Error uploading PDF:", err);
            alert("Fehler beim Hochladen der PDF.");
        } finally {
            setUploadingPdf(false);
        }
    };

    const closeRegistrationsModal = () => {
        setViewingRegistrations(null);
        setRegistrations([]);
    };

    if (loading) return <div>Lade Events...</div>;
    if (error) return <div className="text-red-500">{error}</div>;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const futureEvents = events.filter(e => e.status !== 'draft' && new Date(e.date) >= now).sort((a, b) => new Date(a.date) - new Date(b.date));
    const pastEvents = events.filter(e => e.status !== 'draft' && new Date(e.date) < now).sort((a, b) => new Date(b.date) - new Date(a.date));
    const draftEvents = events.filter(e => e.status === 'draft').sort((a, b) => new Date(a.date) - new Date(b.date));

    if (selectedEventId) {
        const event = events.find(e => e._id === selectedEventId);
        if (event) {
            return (
                <div className="fixed inset-0 z-50 bg-surface flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-outlineVariant">
                        <button onClick={() => setSelectedEventId(null)} className="md3-btn-text gap-2">
                            <X size={20} /> Zurück
                        </button>
                        <h2 className="text-title-large font-bold">{event.title}</h2>
                        <div />
                    </div>
                    <div className="flex-1 overflow-hidden">
                        <EventEditor 
                            eventId={selectedEventId} 
                            onClose={() => {
                                setSelectedEventId(null);
                                fetchEvents();
                            }}
                            allUsers={allUsers}
                        />
                    </div>
                </div>
            );
        }
    }

    const renderEventList = (eventList) => (
        <div className="grid grid-cols-1 gap-4">
            {eventList.length === 0 ? (
                <div className="text-center py-12 bg-surface-variant/20 rounded-xl border border-dashed border-outline/30">
                    <Calendar sx={{ fontSize: 48 }} className="mx-auto mb-3 opacity-20" />
                    <p className="text-surface-onVariant font-medium">Keine Events vorhanden.</p>
                </div>
            ) : (
                eventList.map(event => (
                    <div 
                        key={event._id} 
                        onClick={() => navigate(`/events/edit/${event._id}`)}
                        className="md3-card-outlined p-4 !bg-surface-containerLow flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 group cursor-pointer hover:bg-surface-containerHigh transition-colors"
                    >
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider
                                    ${event.category === 'Lager' ? 'bg-tertiary-container text-tertiary-onContainer' :
                                        event.category === 'Gruppenstunde' ? 'bg-primary-container text-primary-onContainer' :
                                            'bg-secondary-container text-secondary-onContainer'}`}>
                                    {event.category}
                                </span>
                                <h4 className="text-title-medium font-bold text-surface-on">{event.title}</h4>
                            </div>
                            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-surface-onVariant">
                                <div className="flex items-center gap-1.5">
                                    <Calendar sx={{ fontSize: 16 }} className="text-primary" />
                                    <span className="font-medium">{new Date(event.date).toLocaleDateString('de-DE')}</span>
                                </div>
                                {(event.startTime || event.endTime) && (
                                    <div className="flex items-center gap-1.5">
                                        <Clock sx={{ fontSize: 16 }} className="text-primary" />
                                        <span className="font-medium">{event.startTime} {event.endTime ? `– ${event.endTime}` : ''}</span>
                                    </div>
                                )}
                                {event.location && (
                                    <div className="flex items-center gap-1.5">
                                        <MapPin sx={{ fontSize: 16 }} className="text-primary" />
                                        <span className="font-medium">{event.location}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 self-end sm:self-center">
                            {event.status === 'draft' ? (
                                <span className="px-2.5 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-full text-xs font-bold border border-amber-500/20">
                                    Entwurf
                                </span>
                            ) : (
                                <span className="px-2.5 py-1 bg-green-500/10 text-green-700 dark:text-green-400 rounded-full text-xs font-bold border border-green-500/20">
                                    Veröffentlicht
                                </span>
                            )}
                            {event.isRegistrationEnabled && (
                                <div className="flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-bold">
                                    <Users size={14} />
                                    <span>Anmeldungen</span>
                                </div>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/blog/new?eventId=${event._id}&title=${encodeURIComponent(`Rückblick: ${event.title}`)}`);
                                }}
                                className="md3-btn-tonal text-xs py-1.5 px-3 flex items-center gap-1 !bg-secondary-container !text-secondary-onContainer"
                                title="Rückblick / Blogbeitrag zu diesem Event verfassen"
                            >
                                <FileEdit sx={{ fontSize: 16 }} />
                                <span>Rückblick</span>
                            </button>
                            {event.status === 'draft' && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handlePublish(event._id); }}
                                    className="md3-btn-filled text-xs py-1.5 px-3 !bg-primary !text-primary-on shadow-elevation-1"
                                    title="Jetzt veröffentlichen"
                                >
                                    Veröffentlichen
                                </button>
                            )}
                            <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/events/edit/${event._id}`); }}
                                className="md3-btn-tonal text-xs py-1.5 px-3"
                            >
                                Bearbeiten
                            </button>
                            {(user.role === 'admin' || (event.createdBy?._id || event.createdBy) === user._id) && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(event._id); }}
                                    className="md3-icon-btn !text-error hover:bg-error/10"
                                    title="Löschen"
                                >
                                    <Trash2 sx={{ fontSize: 20 }} />
                                </button>
                            )}
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    return (
        <div className="md3-card-elevated p-6 mb-8">
            <div className="flex justify-between items-center mb-6">
                <div className="flex gap-2 p-1 bg-surface-container rounded-full">
                    <button
                        onClick={() => setActiveTab('future')}
                        className={`px-6 py-2 rounded-full text-label-large font-bold transition-all ${activeTab === 'future' ? 'bg-primary text-primary-on shadow-elevation-1' : 'text-surface-onVariant hover:bg-surface-on/5'}`}
                    >
                        Anstehende Events
                    </button>
                    <button
                        onClick={() => setActiveTab('past')}
                        className={`px-6 py-2 rounded-full text-label-large font-bold transition-all ${activeTab === 'past' ? 'bg-primary text-primary-on shadow-elevation-1' : 'text-surface-onVariant hover:bg-surface-on/5'}`}
                    >
                        Vergangene Events
                    </button>
                    <button
                        onClick={() => setActiveTab('drafts')}
                        className={`px-6 py-2 rounded-full text-label-large font-bold transition-all ${activeTab === 'drafts' ? 'bg-primary text-primary-on shadow-elevation-1' : 'text-surface-onVariant hover:bg-surface-on/5'}`}
                    >
                        Entwürfe
                    </button>
                </div>
                <button
                    onClick={() => navigate('/events/edit/new')}
                    className="md3-btn-filled gap-2 !bg-primary !text-primary-on"
                >
                    <Plus sx={{ fontSize: 20 }} />
                    Neues Event
                </button>
            </div>

            {showCreateForm && (
                <div className="md3-card-filled p-6 mb-8 border border-outlineVariant">
                    <h3 className="text-title-large font-bold mb-4 text-surface-on">
                        {editingEvent ? 'Event bearbeiten' : 'Neues Event erstellen'}
                    </h3>
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md3-textfield">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Titel*</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all"
                                />
                            </div>
                            <div className="md3-textfield">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Datum*</label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all"
                                />
                            </div>
                            <div className="md3-textfield">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Startzeit</label>
                                <input
                                    type="time"
                                    name="startTime"
                                    value={formData.startTime}
                                    onChange={handleInputChange}
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all"
                                />
                            </div>
                            <div className="md3-textfield">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Endzeit</label>
                                <input
                                    type="time"
                                    name="endTime"
                                    value={formData.endTime}
                                    onChange={handleInputChange}
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all"
                                />
                            </div>
                            <div className="md3-textfield">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Ort</label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all"
                                />
                            </div>
                            <div className="md3-textfield">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">URL-Slug (e.g. sommer-lager-2026)</label>
                                <input
                                    type="text"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleInputChange}
                                    placeholder="automatisch-generiert"
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all font-mono text-sm"
                                />
                            </div>
                            <div className="md3-textfield font-bold">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Kategorie*</label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all"
                                >
                                    <option value="Sonstiges">Sonstiges</option>
                                    <option value="Gruppenstunde">Gruppenstunde</option>
                                    <option value="Lager">Lager</option>
                                    <option value="Aktion">Aktion</option>
                                </select>
                            </div>
                            <div className="md:col-span-2 md3-textfield">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Beschreibung</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all"
                                />
                            </div>
                            <div className="md:col-span-2 md3-textfield">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Link (Optional)</label>
                                <input
                                    type="url"
                                    name="link"
                                    value={formData.link}
                                    onChange={handleInputChange}
                                    placeholder="https://..."
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Bild (Optional)</label>
                                <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl bg-surface-container/50 border border-outlineVariant">
                                    {formData.imageUrl ? (
                                        <div className="relative w-32 h-20 rounded-lg overflow-hidden shrink-0 border border-outlineVariant">
                                            <img src={formData.imageUrl} alt="Event Preview" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                                                className="absolute inset-0 bg-black/40 flex items-center justify-center text-white opacity-0 hover:opacity-100 transition-opacity"
                                            >
                                                <Trash2 sx={{ fontSize: 18 }} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-32 h-20 rounded-lg bg-surface-containerHighest flex items-center justify-center text-surface-onVariant shrink-0 border border-dashed border-outline/30">
                                            <Calendar sx={{ fontSize: 24 }} />
                                        </div>
                                    )}
                                    <div className="flex-grow w-full space-y-2">
                                        <div className="flex flex-wrap gap-2">
                                            <label className="md3-btn-tonal text-xs cursor-pointer py-1.5 px-3 flex items-center gap-1.5">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (!file) return;
                                                        setUploadingImage(true);
                                                        const fd = new FormData();
                                                        fd.append('file', file);
                                                        try {
                                                            const config = {
                                                                headers: {
                                                                    'Content-Type': 'multipart/form-data',
                                                                    Authorization: `Bearer ${user.token}`,
                                                                },
                                                            };
                                                            const res = await axios.post(`${API_URL}/files/upload`, fd, config);
                                                            setFormData(prev => ({ ...prev, imageUrl: res.data.url }));
                                                        } catch (err) {
                                                            console.error('Image upload failed:', err);
                                                            alert('Fehler beim Hochladen des Bildes.');
                                                        } finally {
                                                            setUploadingImage(false);
                                                        }
                                                    }}
                                                    className="hidden"
                                                    disabled={uploadingImage}
                                                />
                                                {uploadingImage ? <RefreshCw className="animate-spin" size={14} /> : <ImageIcon size={14} />}
                                                <span>Bild hochladen</span>
                                            </label>
                                            {formData.imageUrl && (
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                                                    className="md3-btn-text text-xs text-error py-1.5 px-3 flex items-center gap-1"
                                                >
                                                    <Trash2 size={14} /> Entfernen
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="url"
                                            name="imageUrl"
                                            value={formData.imageUrl}
                                            onChange={handleInputChange}
                                            placeholder="Oder Bild-URL eingeben..."
                                            className="w-full p-2.5 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all text-xs"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Bearbeitungsrechte</label>
                                <p className="text-xs text-surface-onVariant mb-2 px-1">
                                    Wähle aus, wer dieses Event bearbeiten darf:
                                </p>
                                <select
                                    multiple
                                    value={formData.editableBy}
                                    onChange={handlePermissionChange}
                                    className="w-full p-3 bg-surface text-surface-on border border-outline rounded-lg focus:border-2 focus:border-primary focus:outline-none transition-all min-h-[120px]"
                                >
                                    {allUsers
                                        .filter(u => u._id !== user._id)
                                        .map(u => (
                                            <option key={u._id} value={u._id} className="py-1">
                                                {u.userData?.scoutName || u.userData?.firstName || u.email || 'Benutzer'} ({u.role})
                                            </option>
                                        ))}
                                </select>
                                <p className="text-xs text-surface-onVariant mt-1 px-1">
                                    Halte Strg/Cmd gedrückt für Mehrfachauswahl
                                </p>
                            </div>

                            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-on/5 transition-colors cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, requiresConsent: !prev.requiresConsent }))}>
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${formData.requiresConsent ? 'bg-primary border-primary text-primary-on' : 'border-outline'}`}>
                                        {formData.requiresConsent && <CheckCircle sx={{ fontSize: 14 }} className="text-inherit" />}
                                    </div>
                                    <span className="text-body-medium text-surface-on">Einverständniserklärung erforderlich</span>
                                </div>
                                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-on/5 transition-colors cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, isRegistrationEnabled: !prev.isRegistrationEnabled }))}>
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${formData.isRegistrationEnabled ? 'bg-primary border-primary text-primary-on' : 'border-outline'}`}>
                                        {formData.isRegistrationEnabled && <CheckCircle sx={{ fontSize: 14 }} className="text-inherit" />}
                                    </div>
                                    <span className="text-body-medium text-surface-on">Anmeldung aktivieren</span>
                                </div>
                                {/* Eigene Detailseite ist standardmäßig immer aktiv */}

                                {formData.isRegistrationEnabled && (
                                    <>
                                        <div className="flex items-center gap-3 p-3 ml-4 rounded-lg hover:bg-surface-on/5 transition-colors cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, requiresApproval: !prev.requiresApproval }))}>
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${formData.requiresApproval ? 'bg-primary border-primary text-primary-on' : 'border-outline'}`}>
                                                {formData.requiresApproval && <CheckCircle sx={{ fontSize: 14 }} className="text-inherit" />}
                                            </div>
                                            <span className="text-body-medium text-surface-on">Manuelle Freigabe nötig</span>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 ml-4 rounded-lg hover:bg-surface-on/5 transition-colors cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, askHealthQuestions: !prev.askHealthQuestions }))}>
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${formData.askHealthQuestions ? 'bg-primary border-primary text-primary-on' : 'border-outline'}`}>
                                                {formData.askHealthQuestions && <CheckCircle sx={{ fontSize: 14 }} className="text-inherit" />}
                                            </div>
                                            <span className="text-body-medium text-surface-on">Gesundheitsdaten abfragen</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Status Selection */}
                            <div className="md:col-span-2 mt-4 p-4 bg-surface-containerHigh rounded-lg border border-outlineVariant">
                                <label className="block text-label-large font-bold text-surface-on mb-3">Status</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="status"
                                            value="draft"
                                            checked={formData.status === 'draft' || !formData.status}
                                            onChange={handleInputChange}
                                            className="w-4 h-4 text-primary focus:ring-primary bg-surface border-outline"
                                        />
                                        <span className="text-body-medium text-surface-on">Entwurf (Unsichtbar)</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="status"
                                            value="published"
                                            checked={formData.status === 'published'}
                                            onChange={handleInputChange}
                                            className="w-4 h-4 text-primary focus:ring-primary bg-surface border-outline"
                                        />
                                        <span className="text-body-medium text-surface-on font-bold">Veröffentlicht</span>
                                    </label>
                                </div>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-label-medium text-surface-onVariant mb-1 ml-1">Zusätzliche Unterlagen (PDF)</label>
                                {formData.consentFormUrl ? (
                                    <div className="flex items-center justify-between p-3 bg-primary-container text-primary-onContainer rounded-lg border border-primary/20">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle sx={{ fontSize: 18 }} />
                                            <a href={formData.consentFormUrl} target="_blank" rel="noopener noreferrer" className="text-sm underline font-medium">
                                                PDF Anzeigen
                                            </a>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, consentFormUrl: '' }))}
                                            className="p-1 hover:bg-primary-onContainer/10 rounded-full transition-colors"
                                        >
                                            <Trash2 sx={{ fontSize: 18 }} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            onChange={handlePdfUpload}
                                            disabled={uploadingPdf}
                                            className="w-full p-2.5 bg-surface text-surface-on border border-outline border-dashed rounded-lg file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-secondary-container file:text-secondary-onContainer hover:file:brightness-95 cursor-pointer"
                                        />
                                    </div>
                                )}
                                {uploadingPdf && <p className="text-xs text-primary mt-1 animate-pulse">Wird hochgeladen...</p>}
                            </div>
                        </div>
                        <div className="mt-8 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="md3-btn-text !text-surface-on"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="submit"
                                className="md3-btn-filled !bg-primary !text-primary-on"
                            >
                                {editingEvent ? 'Aktualisieren' : 'Erstellen'}
                            </button>
                        </div>
                    </form >
                </div >
            )}

            {renderEventList(activeTab === 'future' ? futureEvents : activeTab === 'past' ? pastEvents : draftEvents)}

            {
                editingEventPage && (
                    <EventPageBuilder
                        event={editingEventPage}
                        allUsers={allUsers}
                        onUpdate={() => {
                            fetchEvents();
                            setEditingEventPage(null);
                        }}
                        onClose={() => setEditingEventPage(null)}
                    />
                )
            }

            {
                viewingRegistrations && (
                    <div className="md3-dialog-scrim animate-fade-in flex items-center justify-center p-4">
                        <div className="md3-card-elevated w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-scale-in !bg-surface">
                            <div className="flex justify-between items-center p-6 border-b border-outlineVariant">
                                <h3 className="text-headline-small font-bold text-surface-on">Anmeldungen: {viewingRegistrations.title}</h3>
                                <button onClick={closeRegistrationsModal} className="md3-icon-btn !text-surface-on">
                                    <X sx={{ fontSize: 24 }} />
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto flex-1">
                                {loadingRegistrations ? (
                                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                                        <div className="md3-progress-circular" />
                                        <p className="text-surface-onVariant">Lade Anmeldungen...</p>
                                    </div>
                                ) : registrations.length === 0 ? (
                                    <p className="text-center py-12 text-surface-onVariant italic">Keine Anmeldungen vorhanden.</p>
                                ) : (
                                    <div className="space-y-6">
                                        <div className="flex flex-wrap gap-4">
                                            <div className="p-4 bg-primary-container text-primary-onContainer rounded-xl flex-1 min-w-[200px] border border-primary/10">
                                                <div className="text-label-medium uppercase font-bold opacity-70">Gesamt</div>
                                                <div className="text-headline-medium font-black">{registrations.length}</div>
                                            </div>
                                            <div className="p-4 bg-tertiary-container text-tertiary-onContainer rounded-xl flex-1 min-w-[200px] border border-tertiary/10">
                                                <div className="text-label-medium uppercase font-bold opacity-70">Ausstehend</div>
                                                <div className="text-headline-medium font-black">{registrations.filter(r => r.status === 'pending').length}</div>
                                            </div>
                                        </div>
                                        <div className="overflow-x-auto rounded-xl border border-outlineVariant bg-surface-containerLowest">
                                            <table className="min-w-full divide-y divide-outlineVariant">
                                                <thead>
                                                    <tr className="bg-surface-containerHigh">
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-surface-onVariant uppercase tracking-widest">Name</th>
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-surface-onVariant uppercase tracking-widest">E-Mail</th>
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-surface-onVariant uppercase tracking-widest">Status</th>
                                                        <th className="px-6 py-4 text-left text-xs font-bold text-surface-onVariant uppercase tracking-widest">Zusatzinfos</th>
                                                        <th className="px-6 py-4 text-right text-xs font-bold text-surface-onVariant uppercase tracking-widest">Aktionen</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-outlineVariant">
                                                    {registrations.map((reg) => (
                                                        <tr key={reg._id} className="hover:bg-surface-on/5 transition-colors">
                                                            <td className="px-6 py-4 whitespace-nowrap text-surface-on font-medium">
                                                                {reg.user ? (reg.user.userData?.scoutName || reg.user.userData?.firstName || reg.user.email) : reg.guestName}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-surface-onVariant text-sm">
                                                                {reg.user ? reg.user.email : reg.guestEmail}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                {reg.status === 'pending' ? (
                                                                    <span className="md3-badge-large !bg-tertiary !text-tertiary-on px-2 rounded-full">Ausstehend</span>
                                                                ) : reg.status === 'confirmed' ? (
                                                                    <span className="md3-badge-large !bg-primary !text-primary-on px-2 rounded-full">Bestätigt</span>
                                                                ) : (
                                                                    <span className="md3-badge-large !bg-error !text-error-on px-2 rounded-full">Abgelehnt</span>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 text-xs text-surface-onVariant">
                                                                {reg.additionalData && Object.keys(reg.additionalData).length > 0 ? (
                                                                    <div className="flex flex-col gap-1 max-w-[250px]">
                                                                        {reg.additionalData.registrationType === "children" && reg.additionalData.children && Array.isArray(reg.additionalData.children) && (
                                                                            <div className="mb-2 p-2 bg-primary/5 rounded-lg border border-primary/10">
                                                                                <div className="font-bold text-primary mb-1 flex items-center gap-1">
                                                                                    <Users size={12} />
                                                                                    Angemeldete Kinder:
                                                                                </div>
                                                                                <div className="flex flex-wrap gap-1">
                                                                                    {reg.additionalData.children.map((child, idx) => (
                                                                                        <span key={idx} className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-[10px] font-bold">
                                                                                            {child.name} {child.age ? `(${child.age} J.)` : ""}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {Object.entries(reg.additionalData)
                                                                            .filter(([key]) => key !== "children")
                                                                            .map(([key, val]) => (
                                                                                <div key={key} className="flex gap-2">
                                                                                    <span className="font-bold opacity-70">{key}:</span>
                                                                                    <span className="truncate">{String(val)}</span>
                                                                                </div>
                                                                            ))}
                                                                    </div>
                                                                ) : (
                                                                    <span className="italic opacity-50">Keine</span>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                                {reg.status === 'pending' && (
                                                                    <button
                                                                        onClick={() => handleApproveRegistration(reg._id)}
                                                                        className="md3-btn-tonal h-8 px-4 text-xs gap-1.5"
                                                                    >
                                                                        <CheckCircle sx={{ fontSize: 16 }} />
                                                                        Freigeben
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="p-6 border-t border-outlineVariant bg-surface-container flex justify-end">
                                <button
                                    onClick={closeRegistrationsModal}
                                    className="md3-btn-filled !bg-secondary !text-secondary-on"
                                >
                                    Schließen
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default EventManagement;
